/* eslint-disable no-debugger */
/**
 * Company Information only: validation and inline error display (DOM).
 * This module must not validate or display errors for personal info, address, or any other step.
 * Used for real-time validation on the Company Information page and on Register/Next from that step.
 */

import {
  COMPANY_INFO_PATH_PREFIX,
  validateCompanyInfoFormData,
  validateCompanyInfoFormDataForSaveExit,
  type CompanyValidationError,
} from "./dxpRegistrationValidation";

export const COMPANY_VALIDATION_INLINE_ERROR_CLASS =
  "company-validation-inline-error";

export {
  COMPANY_INFO_PATH_PREFIX,
  type CompanyValidationError,
  validateCompanyInfoFormData,
  validateCompanyInfoFormDataForSaveExit,
} from "./dxpRegistrationValidation";

export const COMPANY_FIELD_LABELS: Record<string, string> = {
  companyInfo_companyName: "Company name",
  companyInfo_website: "Company website URL",
  companyInfo_phone: "Phone",
  companyInfo_primaryBusiness: "How would you best describe your company?",
  companyInfo_ownership: "What best describes your company's ownership model?",
  companyInfo_totalEmployees: "Number of employees (company-wide)",
  companyInfo_address_hqAddress: "Address of company headquarters",
  companyInfo_address_country: "Country/Region",
  companyInfo_address_state: "State",
  companyInfo_address_city: "City",
  companyInfo_address_zipCode: "Zip Code",
  companyInfo_companyDescription: "Detailed company description",
  companyInfo_programBenefits:
    "How do Adobe Digital Experience applications benefit your business?",
  companyInfo_referredBy:
    "Do you have a referral from an Adobe employee? If yes, please enter their email address.",
  companyInfo_linkedin: "LinkedIn profile URL",
};

function normalizeLabelText(t: string): string {
  return (t || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function getFieldWrapperFromLabel(labelEl: Element): Element | null {
  let node: Element | null = labelEl;
  while (node && node !== document.body) {
    const hasControl = node.querySelector(
      "input, textarea, select, [role='combobox'], [role='textbox']",
    );
    if (hasControl) return node;
    node = node.parentElement;
  }
  return (
    labelEl.closest(".spectrum-Field") ||
    labelEl.closest("[class*='Field']") ||
    labelEl.closest(".spectrum-Textfield") ||
    labelEl.closest("[class*='Textfield']") ||
    labelEl.parentElement
  );
}

/**
 * Pure validation: Company Information fields only (see dxpRegistrationValidation).
 */
export function validateCompanyInfo(
  formData: Record<string, unknown> | null | undefined,
): CompanyValidationError[] {
  return validateCompanyInfoFormData(formData);
}

/** Save-exit payload: same as {@link validateCompanyInfo} except optional additional-info fields. */
export function validateCompanyInfoForSaveExit(
  formData: Record<string, unknown> | null | undefined,
): CompanyValidationError[] {
  return validateCompanyInfoFormDataForSaveExit(formData);
}

/**
 * Remove previously injected Company Information validation error elements from the DOM only.
 * Does not clear form library or other step errors.
 */
export function clearCompanyValidationInlineErrors(): void {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll(`.${COMPANY_VALIDATION_INLINE_ERROR_CLASS}`)
    .forEach((el) => el.remove());
}

/** Company field keys we validate (same set we use for required/pattern). */
const COMPANY_FIELD_KEYS = [
  "companyInfo_companyName",
  "companyInfo_website",
  "companyInfo_phone",
  "companyInfo_primaryBusiness",
  "companyInfo_ownership",
  "companyInfo_totalEmployees",
  "companyInfo_address_hqAddress",
  "companyInfo_address_country",
  "companyInfo_address_state",
  "companyInfo_address_city",
  "companyInfo_address_zipCode",
  "companyInfo_companyDescription",
  "companyInfo_programBenefits",
  "companyInfo_referredBy",
  "companyInfo_linkedin",
];

/**
 * True if any company information field has a value (not null, not undefined, not "" after trim).
 * Use this to show DOM errors only after the user has entered something, not on load.
 */
export function hasAnyCompanyFieldValue(
  formData: Record<string, unknown> | null | undefined,
): boolean {
  if (!formData || typeof formData !== "object") return false;
  const trimmed = (v: unknown) => (v != null ? String(v).trim() : "");
  return COMPANY_FIELD_KEYS.some((key) => trimmed(formData[key]) !== "");
}

/**
 * True if any company field has any input (string length > 0), including space-only.
 * Use this to decide whether to run validation so that space-only input is validated and shows errors.
 */
export function hasAnyCompanyFieldInput(
  formData: Record<string, unknown> | null | undefined,
): boolean {
  if (!formData || typeof formData !== "object") return false;
  return COMPANY_FIELD_KEYS.some(
    (key) => String(formData[key] ?? "").length > 0,
  );
}

/** Left nav class: we must never search or insert in this area (User profile / step indicator). */
const LEFT_NAV_SELECTOR = ".left-navigation-container";

/**
 * Return the main content area only (excludes left nav). Use this as root so we never place errors in the left panel.
 */
function getMainContentRoot(): Element {
  const wrapper =
    document.querySelector(".registration-content-wrapper") ??
    document.querySelector(".registration-page-wrapper") ??
    document.body;
  const leftNav = wrapper.querySelector(LEFT_NAV_SELECTOR);
  if (leftNav?.nextElementSibling) {
    return leftNav.nextElementSibling;
  }
  return wrapper;
}

/**
 * Find the DOM section that contains Company Information fields. Only searches main content; never returns left nav.
 */
function findCompanyInformationSection(root: Element): Element | null {
  const byCompanyInput = root.querySelector(
    'input[name="companyInfo_companyName"], [data-field-key="companyInfo_companyName"]',
  );
  if (byCompanyInput) {
    return (
      byCompanyInput.closest("form") ??
      byCompanyInput.closest("[class*='form'], [class*='Form']") ??
      byCompanyInput.closest("section") ??
      byCompanyInput.closest("div")
    );
  }
  const headings = root.querySelectorAll(
    "h1, h2, h3, [role='heading'], strong",
  );
  const companyHeading = Array.from(headings).find((el) => {
    if ((el.textContent ?? "").trim() !== "Company information") return false;
    if (el.closest(LEFT_NAV_SELECTOR)) return false;
    return true;
  });
  if (companyHeading) {
    const section =
      companyHeading.closest("section") ?? companyHeading.closest("div");
    return section ?? companyHeading.parentElement;
  }
  return null;
}

function findFieldElement(
  root: Element,
  fieldKey: string,
  labelSearchScope?: Element,
): Element | null {
  const scope = labelSearchScope ?? root;
  const trySelectors = [
    `[data-field-key="${fieldKey}"]`,
    `[id="${fieldKey}-input"]`,
    `input[name="${fieldKey}"]`,
    `textarea[name="${fieldKey}"]`,
    `[id="${fieldKey}"]`,
    `[id="root_${fieldKey}"]`,
    `[name="${fieldKey}"]`,
    `[data-path="${fieldKey}"]`,
    `[data-path="/${fieldKey}"]`,
  ];
  const bySelector = trySelectors.map((sel) => scope.querySelector(sel));
  const found = bySelector.find((el) => el != null);
  if (found) {
    const isControl = found.matches(
      "input, textarea, select, [role='combobox'], [role='textbox']",
    );
    const controlInside =
      !isControl &&
      found.querySelector(
        `input[name="${fieldKey}"], textarea[name="${fieldKey}"], [data-field-key="${fieldKey}"]`,
      );
    if (controlInside) {
      const wrapper =
        (controlInside as Element).closest(".spectrum-Field") ||
        (controlInside as Element).closest("[class*='Field']") ||
        (controlInside as Element).closest(".spectrum-Textfield") ||
        (controlInside as Element).closest("[class*='Textfield']");
      return (wrapper as Element) ?? (controlInside as Element);
    }
    if (isControl) return found;
    // Container found but doesn't contain the field's control (e.g. wrong id on section) – use label search instead
    const hasCorrectControl = found.querySelector(
      `[name="${fieldKey}"], [data-field-key="${fieldKey}"]`,
    );
    if (hasCorrectControl) return found;
  }
  if (scope !== root) {
    const elInRoot = root.querySelector(trySelectors[0]);
    if (elInRoot) return elInRoot;
  }
  const labelText = COMPANY_FIELD_LABELS[fieldKey];
  if (!labelText) return null;
  const searchNorm = normalizeLabelText(labelText).slice(0, 25);
  const byAria = scope.querySelector(
    `[aria-label*="${labelText.slice(0, 20).replace(/"/g, "")}"]`,
  );
  if (byAria) return byAria;
  const labels = scope.querySelectorAll(
    "label, [class*='label'], [class*='Label'], .spectrum-FieldLabel, [class*='FieldLabel']",
  );
  const byLabel = Array.from(labels).find((label) => {
    const raw = (label as HTMLElement).innerText ?? label.textContent ?? "";
    const text = normalizeLabelText(raw);
    if (text.length < 3) return false;
    // Exact or prefix match so "City" matches "City *" but not "Country/Region" or text that only contains "city" elsewhere
    const exactOrStartsWithField =
      text === searchNorm ||
      (text.startsWith(searchNorm) &&
        (text.length === searchNorm.length ||
          /[\s*)]/.test(text[searchNorm.length] ?? "")));
    if (exactOrStartsWithField) return true;
    // For short labels (e.g. City, State, Country, Zip), do NOT use includes(searchNorm) so we
    // never match a parent control whose full text contains "city" (e.g. Country dropdown with "City" in options)
    if (searchNorm.length <= 6) return false;
    const prefix = searchNorm.slice(0, 10);
    const looseMatch =
      text.includes(searchNorm) ||
      searchNorm.includes(text.slice(0, 20)) ||
      (text.startsWith(prefix) && text.length >= prefix.length);
    return looseMatch;
  });
  if (byLabel) {
    const field = getFieldWrapperFromLabel(byLabel);
    if (field) return field;
  }
  return null;
}

/**
 * Show Company Information validation errors below each company field (or as banner if field not found).
 * Only processes errors whose instancePath starts with COMPANY_INFO_PATH_PREFIX; ignores all other paths.
 * Pass empty array to only clear (no new errors).
 */
export function showCompanyValidationErrorsBelowFields(
  errors: CompanyValidationError[],
  options?: { root?: Element | null; scrollToFirst?: boolean },
): void {
  if (typeof document === "undefined") return;
  clearCompanyValidationInlineErrors();
  const companyOnly = errors.filter((e) =>
    e.instancePath.startsWith(COMPANY_INFO_PATH_PREFIX),
  );
  if (companyOnly.length === 0) return;

  const phoneErrors = errors.filter((e) =>
    e.instancePath.startsWith("/companyInfo_phone"),
  );

  const root = options?.root ?? getMainContentRoot();

  const companySection = findCompanyInformationSection(root);
  const searchRoot = companySection ?? root;

  let firstInsertedTarget: Element | null = null;
  companyOnly.forEach(({ instancePath, message }) => {
    const fieldKey = instancePath.replace(/^\//, "");
    const el =
      findFieldElement(root, fieldKey, searchRoot) ??
      findFieldElement(root, fieldKey, root);
    // Only show errors for fields that exist in the DOM
    if (!el) return;

    const errorEl = document.createElement("div");
    errorEl.className = COMPANY_VALIDATION_INLINE_ERROR_CLASS;
    errorEl.setAttribute("role", "alert");
    errorEl.style.cssText =
      "color: var(--spectrum-semantic-negative-color-text-default, #d7373f); font-size: 12px; margin-top: 4px; margin-bottom: 8px;";
    errorEl.textContent = message;

    const fieldWrapper =
      el.closest(".spectrum-Field") ||
      el.closest("[class*='Field']") ||
      el.closest(".spectrum-Textfield") ||
      el.closest("[class*='Textfield']") ||
      el.closest("[class*='Control']") ||
      getFieldWrapperFromLabel(el) ||
      (el as HTMLElement).parentElement;
    const insertTarget = fieldWrapper || el;
    insertTarget.after(errorEl);
    if (!firstInsertedTarget) firstInsertedTarget = insertTarget;
  });
  if (options?.scrollToFirst !== false && firstInsertedTarget) {
    (firstInsertedTarget as Element).scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  } else if (options?.scrollToFirst !== false && phoneErrors.length > 0) {
    const phoneEl = root.querySelector('[data-field-key="companyInfo_phone"]');
    if (phoneEl) {
      phoneEl.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }
}

/**
 * Schedule showing Company Information validation errors (e.g. after Register/Next from company step) with a retry so DOM is ready.
 */
export function scheduleShowCompanyValidationErrorsBelowFields(
  errors: CompanyValidationError[],
): void {
  const companyOnly = errors.filter((e) =>
    e.instancePath.startsWith(COMPANY_INFO_PATH_PREFIX),
  );
  if (companyOnly.length === 0) return;
  const run = () =>
    showCompanyValidationErrorsBelowFields(companyOnly, {
      scrollToFirst: true,
    });
  setTimeout(run, 400);
  setTimeout(() => {
    clearCompanyValidationInlineErrors();
    run();
  }, 800);
}
